// ── Pure utility functions for the ScopeMigration module ───────────────────
import type {
  WorkflowScope,
  ManifestScope,
  ScopeManifest,
  PreviewSummary,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// Manifest Ingest Pipeline — DRY stages reusable for file-input, drag-drop,
// paste, and any future ingestion vector.
// ═══════════════════════════════════════════════════════════════════════════

export interface IngestResult {
  manifest: ScopeManifest | null;
  error: string | null;
}

// ── Stage 1: File-type gate ──────────────────────────────────────────────────
const ACCEPTED_MIME = new Set(['application/json', '']);
const ACCEPTED_EXT = /\.json$/i;

export function validateFileType(file: File): string | null {
  if (!ACCEPTED_MIME.has(file.type) && !ACCEPTED_EXT.test(file.name)) {
    return `Unsupported file type "${file.type || file.name}". Only .json manifest files are accepted.`;
  }
  return null;
}

// ── Stage 2: Read file → string ─────────────────────────────────────────────
export function readFileContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file "${file.name}".`));
    reader.readAsText(file);
  });
}

// ── Stage 3: Parse JSON ─────────────────────────────────────────────────────
export function parseJson(raw: string): { data: unknown; error: string | null } {
  try {
    return { data: JSON.parse(raw), error: null };
  } catch (e) {
    return { data: null, error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }
}

// ── Stage 4: Schema sanity ──────────────────────────────────────────────────
export function validateManifestSchema(obj: unknown): IngestResult {
  if (!obj || typeof obj !== 'object') {
    return { manifest: null, error: 'Parsed value is not an object.' };
  }
  const o = obj as Record<string, unknown>;
  if (!o.version) {
    return { manifest: null, error: 'Missing required field "version".' };
  }
  if (!Array.isArray(o.scopes)) {
    return { manifest: null, error: 'Missing or invalid "scopes" array.' };
  }
  if (o.scopes.length === 0) {
    return { manifest: null, error: '"scopes" array is empty — nothing to import.' };
  }
  // Spot-check first scope entry structure
  const first = o.scopes[0] as Record<string, unknown> | undefined;
  if (!first?.scope_namespace || !first?.scope_key) {
    return { manifest: null, error: 'First scope entry missing "scope_namespace" or "scope_key".' };
  }
  return { manifest: obj as ScopeManifest, error: null };
}

// ── Combined pipeline: File → IngestResult ──────────────────────────────────
export async function ingestManifestFile(file: File): Promise<IngestResult> {
  const typeErr = validateFileType(file);
  if (typeErr) return { manifest: null, error: typeErr };
  const raw = await readFileContent(file);
  return ingestManifestText(raw);
}

// ── Combined pipeline: raw string → IngestResult ────────────────────────────
export function ingestManifestText(raw: string): IngestResult {
  if (!raw.trim()) return { manifest: null, error: null };
  const { data, error: jsonErr } = parseJson(raw);
  if (jsonErr) return { manifest: null, error: jsonErr };
  return validateManifestSchema(data);
}

// ── Scope field normalisers ──────────────────────────────────────────────────

/** Normalise the two field-name variants the API may return. */
export function _ns(s: WorkflowScope): string {
  return s.scope_namespace ?? (s as any).scopeNamespace ?? '';
}
export function _sk(s: WorkflowScope): string {
  return s.scope_key ?? (s as any).scopeKey ?? '';
}

// ── Scope key decoding ───────────────────────────────────────────────────────

/** Decode a scope key like `v1:eyJ2Ijox...` → human-readable field values. */
export function decodeScopeKey(
  raw: string,
): { label: string; fields: [string, string][] } | null {
  const m = raw.match(/^v\d+:(.+)$/);
  if (!m) return null;
  try {
    const json = atob(m[1]);
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed?.fields)) {
      const fields: [string, string][] = parsed.fields;
      const label = fields.map(([, v]) => v).join(' · ');
      return { label, fields };
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        ([k]) => k !== 'v' && k !== 'version',
      );
      if (entries.length > 0) {
        const fields: [string, string][] = entries.map(([k, v]) => [k, String(v)]);
        const label = fields.map(([, v]) => v).join(' · ');
        return { label, fields };
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ── Display helpers ──────────────────────────────────────────────────────────

export function sanitizeName(v: string): string {
  return v
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function datePrefix(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
}

/** Human-readable display for a scope key row. */
export function scopeKeyDisplay(s: WorkflowScope): string {
  const raw = _sk(s);
  const decoded = decodeScopeKey(raw);
  if (!decoded) return raw;
  const prefix = datePrefix(s.created_at);
  const slug = decoded.fields.map(([, v]) => sanitizeName(v)).join('_');
  return prefix ? `${prefix}_${slug}` : slug;
}

/** Same for ManifestScope. */
export function manifestScopeDisplay(s: ManifestScope): string {
  const decoded = decodeScopeKey(s.scope_key);
  if (!decoded) return s.scope_key;
  const prefix = datePrefix(s.created_at);
  const slug = decoded.fields.map(([, v]) => sanitizeName(v)).join('_');
  return prefix ? `${prefix}_${slug}` : slug;
}

// ── Manifest merge ───────────────────────────────────────────────────────────

/** Merge multiple export manifests into one (dedupes scopes by ns+key). */
export function mergeManifests(manifests: ScopeManifest[]): ScopeManifest {
  if (manifests.length === 0) throw new Error('No manifests to merge');
  if (manifests.length === 1) return manifests[0];

  const base = manifests[0];
  const scopeMap = new Map<string, ManifestScope>();
  const outcomeKeys = new Set<string>();
  const outcomes: NonNullable<ScopeManifest['outcomes']> = [];
  const hotStreamKeys = new Set<string>();
  const hotStream: NonNullable<ScopeManifest['hot_stream']> = [];
  const archiveKeys = new Set<string>();
  const hotArchiveFiles: NonNullable<ScopeManifest['hot_archive_files']> = [];

  for (const m of manifests) {
    for (const s of m.scopes) {
      const k = `${s.scope_namespace}\x00${s.scope_key}`;
      if (!scopeMap.has(k)) scopeMap.set(k, s);
    }
    for (const o of m.outcomes ?? []) {
      const k = `${o.scope_ref.scope_namespace}\x00${o.scope_ref.scope_key}\x00${o.handler_key}\x00${o.created_at}`;
      if (!outcomeKeys.has(k)) {
        outcomeKeys.add(k);
        outcomes.push(o);
      }
    }
    for (const h of m.hot_stream ?? []) {
      const k = `${h.scope_ns}\x00${h.scope_key}\x00${h.ts}\x00${h.id_category}\x00${h.id_type}`;
      if (!hotStreamKeys.has(k)) {
        hotStreamKeys.add(k);
        hotStream.push(h);
      }
    }
    for (const f of m.hot_archive_files ?? []) {
      if (!archiveKeys.has(f.rel_path)) {
        archiveKeys.add(f.rel_path);
        hotArchiveFiles.push(f);
      }
    }
  }

  return {
    ...base,
    root_scope: {
      scope_namespace: base.root_scope.scope_namespace,
      scope_key:
        manifests.length > 1
          ? `multi_${manifests.length}_roots`
          : base.root_scope.scope_key,
    },
    scopes: Array.from(scopeMap.values()),
    outcomes,
    hot_stream: hotStream.length > 0 ? hotStream : undefined,
    hot_archive_files: hotArchiveFiles.length > 0 ? hotArchiveFiles : undefined,
  };
}

// ── Preview summary computation ──────────────────────────────────────────────

/**
 * Compute preview summary from all selected + cached scopes.
 * Pure function extracted from the main component's useMemo.
 */
export function computePreviewSummary(
  allSelectedScopes: WorkflowScope[],
  allCachedScopes: WorkflowScope[],
): PreviewSummary | null {
  if (allSelectedScopes.length === 0) return null;

  const byParent = new Map<number, WorkflowScope[]>();
  for (const s of allCachedScopes) {
    if (s.id_scope_father != null) {
      const arr = byParent.get(s.id_scope_father) ?? [];
      arr.push(s);
      byParent.set(s.id_scope_father, arr);
    }
  }

  const nsSet = new Set<string>();
  let totalDescendants = 0;
  let maxDepth = 0;
  let totalDirectChildren = 0;
  const firstChildren: WorkflowScope[] = [];
  const nsCounts = new Map<string, number>();
  let scopesWithHotStream = 0;

  for (const root of allSelectedScopes) {
    const ns = _ns(root);
    nsSet.add(ns);
    nsCounts.set(ns, (nsCounts.get(ns) ?? 0) + 1);
    if (root.last_hot_stream_at) scopesWithHotStream++;
    const walk = (parentId: number, depth: number) => {
      const kids = byParent.get(parentId) ?? [];
      for (const k of kids) {
        if (k.id_scope === parentId) continue;
        totalDescendants++;
        nsSet.add(_ns(k));
        if (k.last_hot_stream_at) scopesWithHotStream++;
        if (depth + 1 > maxDepth) maxDepth = depth + 1;
        if (depth === 0) {
          totalDirectChildren++;
          if (firstChildren.length < 10) firstChildren.push(k);
        }
        walk(k.id_scope!, depth + 1);
      }
    };
    walk(root.id_scope!, 0);
  }

  const totalScopes = allSelectedScopes.length + totalDescendants;

  return {
    rootCount: allSelectedScopes.length,
    rootDisplays: allSelectedScopes.slice(0, 5).map((s) => scopeKeyDisplay(s)),
    rootNs: _ns(allSelectedScopes[0]),
    childCount: totalDirectChildren,
    children: firstChildren,
    totalDescendants,
    treeDepth: maxDepth,
    distinctNamespaces: Array.from(nsSet),
    namespaceCounts: Array.from(nsCounts.entries()).map(([ns, count]) => ({
      ns,
      count,
    })),
    totalScopes,
    scopesWithHotStream,
    // Heuristic estimates — replaced by actual counts after export completes
    estimatedOutcomeCount: totalScopes,
    estimatedHotStreamCount: scopesWithHotStream > 0
      ? scopesWithHotStream    // conservative: at least this many rows
      : undefined,
    // Artifact counts — only available after export (enrichment populates size_bytes).
    artifactFileCount: 0,
    artifactTotalBytes: 0,
  };
}
